import { SmIcon, SmLoading } from '@smiles/smiles-react-design-system';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorMessage, HeaderMobile, Tooltip } from '..';
import { useContextSelector, useDebounce, useLocalStorage } from '../../hooks';
import { Services } from '../../services';
import { ListItem, NotFound } from './components';
import { MOBILE_LABELS } from './constants';

import type { CarSearchFilters, GetSearchLocation, SearchInputType } from '../../types';

export interface ISearchInputProps {
  id: string;
  filterSearchBy: CarSearchFilters;
  debounceDelay?: number;
  placeholder: string;
  placeholderIcon?: string;
  mobileClearIcon?: boolean;
  label?: string;
  type?: SearchInputType;
  showTooltip?: boolean;
  onLocationSelect: (location: GetSearchLocation, type: SearchInputType) => void;
}

export const SearchInput = ({
  id,
  filterSearchBy,
  debounceDelay = 1000,
  label,
  placeholder,
  placeholderIcon,
  mobileClearIcon = false,
  showTooltip = false,
  type = 'oneway',
  onLocationSelect,
  ...props
}: ISearchInputProps) => {
  const {
    generics: { isMobile },
    search: { location },
    errors: { locationError },
    inputs,
  } = useContextSelector();
  const [searchList, setSearchList] = useState<GetSearchLocation[]>([]);
  const [searchHistory, setSearchHistory] = useLocalStorage<GetSearchLocation[]>('searchHistory2', []);
  const [inputTextValue, setInputTextValue] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isMobile && type === 'dropoff' && inputs.locationDropoff) {
      setIsDropdownOpen(true);
      const input = inputRef.current as HTMLInputElement;
      input.focus();
    }
  }, [isMobile, inputs, type]);

  useEffect(() => {
    const locationText = type === 'oneway' ? location.pickup?.name : location[type]?.name;
    persistInputText(locationText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const persistInputText = (locationText?: string) => {
    if (locationText) {
      setInputTextValue(locationText);
    }
  };

  const updateSearchHistory = (item: GetSearchLocation) => {
    const itemAlreadyExists = searchHistory.some(
      (historyItem: GetSearchLocation) => JSON.stringify(historyItem) === JSON.stringify(item),
    );

    if (itemAlreadyExists) {
      const historyWithoutExistingItem = searchHistory.filter(
        (historyItem: GetSearchLocation) => JSON.stringify(historyItem) !== JSON.stringify(item),
      );

      setSearchHistory([item, ...historyWithoutExistingItem]);
      return;
    }

    if (searchHistory.length >= 3) {
      const arrayWithoutLastItem = searchHistory.slice(0, -1);

      setSearchHistory([item, ...arrayWithoutLastItem]);
    } else {
      setSearchHistory([item, ...searchHistory]);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTextValue = event.target.value;
    setInputTextValue(newTextValue);

    const shouldTriggerSearch = newTextValue.length >= 3;

    if (shouldTriggerSearch) {
      setIsSearchLoading(true);
      handleSearch(newTextValue, filterSearchBy);
    }

    if (!isMobile) {
      setIsDropdownOpen(shouldTriggerSearch);
    }
  };

  const handleInputFocus = () => {
    if (isMobile) {
      setIsSearchLoading(true);
      setIsInputFocused(true);
      setTimeout(() => setIsSearchLoading(false), 500);
    } else if (searchHistory.length > 0) {
      setIsDropdownOpen(true);
    }

    setInputTextValue('');
  };

  const handleInputBlur = () => {
    const locationText = type === 'oneway' ? location.pickup?.name : location[type]?.name;

    if (!isInputFocused) {
      persistInputText(locationText);
    }

    if (!isMobile) {
      setIsDropdownOpen(false);
      setSearchList([]);
    }
  };

  const handleInputClear = () => {
    setInputTextValue('');
    setSearchList([]);
  };

  const handleSearch = useDebounce(async (inputValue: string, filterBy: CarSearchFilters) => {
    const searchResponse = await Services.core.getSearch(inputValue, filterBy);

    if (searchResponse) {
      setIsSearchLoading(false);
      setSearchList(searchResponse);
    }
  }, debounceDelay);

  const handleItemSelect = (selectedLocation: GetSearchLocation) => {
    if (isMobile) {
      const input = document.querySelector(`#search-input-field-${id}`) as HTMLInputElement;
      input.blur();

      setIsInputFocused(false);
    } else {
      setIsDropdownOpen(false);
    }

    setInputTextValue(selectedLocation.name);
    updateSearchHistory(selectedLocation);
    onLocationSelect(selectedLocation, type);
  };

  const handleMobileClose = () => {
    const locationText = type === 'oneway' ? location.pickup?.name : location[type]?.name;
    persistInputText(locationText);
    setIsInputFocused(false);
  };

  return (
    <section className={`search-input-container${isInputFocused ? ' focused' : ''}`} {...props}>
      {isInputFocused && <HeaderMobile label={MOBILE_LABELS[type]} onIconClick={handleMobileClose} />}
      {!isInputFocused && (
        <article className="search-input-label-wrapper">
          <label className="search-input-label" htmlFor={`search-input-field-${id}`}>
            {label}
          </label>

          {showTooltip && (
            <Tooltip
              containerClassName="search-input-label-tooltip-container"
              tooltipClassName="search-input-label-tooltip"
              position="right"
              text="Antecedência mínima de 4h para locações nacionais e internacionais."
              offset={{ right: 4 }}
            >
              <SmIcon className="search-input-label-icon" name="info_outline" type="round" />
            </Tooltip>
          )}
        </article>
      )}

      <section className="search-input-field-wrapper">
        <input
          className={`search-input-field${locationError[type].active ? ' error' : ''}`}
          type="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          ref={inputRef}
          id={`search-input-field-${id}`}
          placeholder={placeholder}
          value={inputTextValue}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
        />

        {isMobile && placeholderIcon && (
          <SmIcon className="search-input-field-placeholder-icon" name={placeholderIcon} type="round" />
        )}

        {isMobile && inputTextValue.length > 0 && isInputFocused && mobileClearIcon && (
          <button className="search-input-field-clear-button" type="button" onClick={handleInputClear}>
            <SmIcon className="search-input-field-clear-button-icon" name="close" type="round" />
          </button>
        )}
      </section>

      <section
        className={`search-input-dropdown-container${isDropdownOpen ? ' open' : ''}`}
        id={`search-input-dropdown-${id}`}
      >
        {isSearchLoading && (
          <section className="search-input-dropdown-loading">
            <SmLoading className="search-input-dropdown-loading-icon" type="primary" loadingSize="small" />
          </section>
        )}

        {!isSearchLoading &&
          (inputTextValue.length >= 3 && searchList.length === 0 ? (
            <NotFound />
          ) : (
            <ul className="search-input-dropdown-list" id={`search-input-dropdown-${id}`}>
              {inputTextValue.length < 3 && (
                <label className="search-input-dropdown-history-label">Você buscou por último:</label>
              )}

              {(inputTextValue.length >= 3 ? searchList : searchHistory)
                .filter((item) => {
                  if (filterSearchBy === 'national') {
                    return item.region !== 'international';
                  }

                  return item;
                })
                .map((item, index) => (
                  <ListItem
                    key={`search-input-dropdown-list-item-${index + 1}`}
                    item={item}
                    onSelect={handleItemSelect}
                  />
                ))}
            </ul>
          ))}
      </section>

      {!isMobile && <ErrorMessage isActive={locationError[type].active} message={locationError[type].message} />}
    </section>
  );
};
